import { Flex, Text, Alert, } from '@smarterservices/smarter-ui';
import React, { useEffect, useRef, useState } from 'react';
import { completeAndGetNextStep, getStep } from './api';
import {
  applyModalityOptions,
  checkExtension,
  checkPermissions,
  checkValidMachineSetup,
  createAndJoinMediaRoom,
  enablePermissions,
  getActiveSessionFromExtension,
  getMediaStart, getSessionFromOnboarding, isTrackPublished,
  pinAndFocusTab,
  raiseToolbars,
  removeModalityOptions,
  renderStepComponent,
  requestInformationalStep,
  startBatteryMonitor,
  registerCurrentTab,
} from '../../helpers/helpers';
import { useNavigate } from 'react-router-dom';
import { config } from '../../config';
import {
  ParamsNotFoundError,
  SpSessionSetupError,
  UserNotFoundError,
} from '../../helpers/definedErrors';
import PaneViewer from '../../components/PaneViewer';
import Loader from '../../components/Common/Loader';
import InvalidStatus from '../../components/InvalidStatus';
import useAppStore from '../../stores/app';
import PageError from '../../components/PageError'


const Onboarding = ({ isReconnect = false, inSession = false }) => {
  const validOnboardingStatuses = ['scheduled', 'onboarding', 'pending reconnection', 'in progress'];
  const GET_STEP_TIMEOUT = 60; // number of seconds before timing out

  const { setPageErrorText } = useAppStore();

  const pageErrorText = useAppStore((state) => state.pageErrorText);

  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTaskComplete, setIsTaskComplete] = useState(false);
  const [checkedForValidSetup, setCheckedForValidSetup] = useState(false);
  const needsIntroStep = useRef(isReconnect);
  const navigate = useNavigate();
  const stepMetadataRef = useRef({});
  const setStepMetadata = (metadata) => {
    stepMetadataRef.current = metadata;
  };

  const {
    initializeApp,
    getSessionStartData,
    getApiClient
  } = useAppStore();

  const sessionStatus = useAppStore((state) => state.sessionStatus);
  const phase = useAppStore((state) => state.phase);
  const loadingText = useAppStore((state) => state.loadingText);

  useEffect(() => {
    useAppStore.getState().setIsInSessionOnboarding(inSession);
    useAppStore.getState().setIsInSessionReconnection(isReconnect);

    // Register this tab as the current onboarding tab
    registerCurrentTab();
  }, [isReconnect, inSession]);

  useEffect(() => {
  }, []);

  // Get the step we are on when the component loads
  useEffect(() => {
    // Setup the listener to listen for extension messages
    const handleMessage = (event) => {
      if (
        event.source === window &&
        event.data?.source === 'SmarterProctoringExtensionMessenger'
      ) {
        const payload = event.data?.payload;
        if (!payload || !payload?.message) return;

        if (payload.message === 'pushToNextStep') {
          handleStep(false);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    // Setup onboarding and set the first step
    const initializeOnboarding = async () => {
      if (!inSession) {
        const isActive = await checkIfSessionActiveElsewhere();
        if (isActive) {
          navigate('/session/session-already-active');
          return;
        }
      }
      try {
        await initializeApp({ type: 'session' });
      } catch (error) {
        console.error('Failed to initialize app:', error);
        // TODO: Consider navigating to an error page or showing an error state
        setPageErrorText('There was a problem loading this page.');
      }
      await handleStep(false);
    };
    initializeOnboarding();

    // remove the window listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  async function checkIfSessionActiveElsewhere() {
    let isActive = false;
    const activeSession = await getActiveSessionFromExtension();
    if (activeSession && activeSession.activeInAnotherTab) {
      console.log('There is already an active session');
      isActive = true;
    }
    return isActive;
  }

  const handleMediaRoom = async () => {
    const isMediaRoomCreated =
      JSON.parse(sessionStorage.getItem('spMediaRoomCreated')) || false;
    if (!isMediaRoomCreated) {
      const params = JSON.parse(sessionStorage.getItem('spParams'));
      if (!params) {
        navigate('/missing-params');
        return;
      }
      const response = await getMediaStart(params.sessionSid, params.token);
      if (!response) {
        // TODO: what do we do if there is no identity?
        throw new UserNotFoundError();
      }

      const sessionData = await getSessionFromOnboarding(params);
      if (!sessionData) {
        throw new SpSessionSetupError();
      }

      let inSessionLockoutPeriod = 0;
      if (sessionData?.series?.reconnection?.inSessionLockoutPeriod) {
        inSessionLockoutPeriod = sessionData.series.reconnection.inSessionLockoutPeriod;
      }

      const initialData = {
        permittedItems: {
          blankPaper: sessionData.examConfiguration.permittedBlankPaper,
          bathroomBreak: sessionData.examConfiguration.permittedBathroomBreak,
          calculator: sessionData.examConfiguration.permittedCalculator,
          dictionary: sessionData.examConfiguration.permittedDictionary,
          earplugs: sessionData.examConfiguration.permittedEarplugs,
          formulaSheet: sessionData.examConfiguration.permittedFormulaSheet,
          notes: sessionData.examConfiguration.permittedNotes,
          textbook: sessionData.examConfiguration.permittedTextbook,
          websites: sessionData.examConfiguration.permittedWebsites
        }
      };

      const result = await createAndJoinMediaRoom(
        params.sessionSid,
        response.websocketUrl,
        response.status,
        inSessionLockoutPeriod,
        initialData,
        response.mediaRoomConfig,
      );

      if (!result) {
        //TODO: Need to add logging here...
        throw new SpSessionSetupError();
      } else {
        sessionStorage.setItem('spMediaRoomCreated', JSON.stringify(result));
        // now connect the session UI to the room, this will ensure the extension creates the room first...
        await useAppStore.getState().getMediaAgent();

      }
    }
  };

  const handleConfigurations = async (configurations = []) => {
    if (!Array.isArray(configurations)) {
      return;
    }
    // make sure that every item in the array is an object
    const isValid = configurations.every(
      (item) => typeof item === 'object' && item !== null
    );
    if (!isValid) {
      return;
    }
    for (const configuration of configurations) {
      // take an action based on the passed in configurations
      // TODO: figure out what to do when any of these fail by coming back false
      if (configuration.name === 'setPermissions') {
        const permissionsEnabled = await checkPermissions();
        if (!permissionsEnabled) {
          const success = await enablePermissions();
          console.log('Enabled Permissions:', success);
        }
      } else if (configuration.name === 'applyModalityOptions') {
        const success = await applyModalityOptions(
          configuration.modalityOptions
        );
        console.log('Applied Modality Options:', {
          success,
          modalityOptions: configuration.modalityOptions,
        });
      } else if (configuration.name === 'removeModalityOptions') {
        const success = await removeModalityOptions(
          configuration.modalityOptions
        );
        console.log('Removed Modality Options:', {
          success,
          modalityOptions: configuration.modalityOptions,
        });
      } else if (configuration.name === 'blockExtensions') {
        const whitelistedExtensions = configuration.whitelistedExtensions || [];
        const success = await applyModalityOptions([
          {
            name: 'blockExtensions',
            whitelistedExtensions
          }
        ]);
        console.log('Blocked Extensions:', success);
      } else if (configuration.name === 'startDisplayMonitor') {
        const success = await applyModalityOptions([
          {
            name: 'onlyOneScreen',
          }
        ]);
        console.log('Started Display Monitor:', success);
      } else if (configuration.name === 'startBatteryMonitor') {
        const success = await startBatteryMonitor(configuration.config);
        console.log('Started Battery Monitor:', {
          success,
          config: configuration.config,
        });
      }
    }
  };

  const isInformationalStep = (stepToCheck) => {
    const informationSteps = [
      'DownloadExtension',
      'ReconnectIntro',
      'ReconnectIntroInSession',
      'UnsupportedDevice',
      'UnsupportedBrowser',
    ];
    return informationSteps.includes(stepToCheck.step.id);
  };
  const getInformationalStepToLoad = async (newStep, params = null) => {
    if (!params) {
      params = JSON.parse(sessionStorage.getItem('spParams'));
      if (!params) {
        console.error('No params were found!');
        throw new ParamsNotFoundError();
      }
    }

    // Informational steps do not always have next buttons, so we are responsible for completing them if needed
    const isNewStepInformational = isInformationalStep(newStep);

    // Check if the user is on the correct OS and Browser
    if (!checkedForValidSetup && !inSession) {
      const result = checkValidMachineSetup(window);
      if (result.valid) {
        // The user is on a valid OS and Browser, so we do not need to check again
        setCheckedForValidSetup(true);
      } else {
        // User is on an invalid setup, return the informational step
        // First, check if the newStep is already set to this, then it was just a refresh
        if (newStep.step.id === result.id) {
          // It was a refresh, so we need to just return the step
          return newStep;
        } else if (isNewStepInformational) {
          // We had a different informational step that we need to complete before moving forward
          await completeAndGetNextStep(newStep.step.journeyStepId, {}, params);
        }
        return await requestInformationalStep(result.id);
      }
    }

    // Check if they are running a reconnect and have not seen the intro step yet
    if (isReconnect && needsIntroStep.current) {
      // They have been shown the intro step, so do not show it again
      needsIntroStep.current = false;

      const id = inSession ? 'ReconnectIntroInSession' : 'ReconnectIntro';
      if (newStep.step.id === id) {
        // It was a refresh, so return the step
        return newStep;
      } else if (isNewStepInformational) {
        // Complete the other informational step to begin moving forward
        await completeAndGetNextStep(newStep.step.journeyStepId, {}, params);
      }
      return await requestInformationalStep(id);
    }

    // Check that the extension is installed and not disabled or removed
    // However, do not do this if in session, or it will try to create another media room
    if (!inSession) {
      const isExtensionInstalled = await checkExtension();

      if (isExtensionInstalled) {
        // Extension is installed, so do the initializing of onboarding
        const sessionStart = await getSessionStartData(params);
        if (!sessionStart) {
          throw new SpSessionSetupError();
        }
        await pinAndFocusTab(sessionStart, params);
        await raiseToolbars(`${config.session.sessionUiUrl}/menu`, 'spInjectedToolbar');
        await handleMediaRoom();
      } else {
        // Extension is NOT installed
        if (newStep.step.id === 'DownloadExtension') {
          // It was a refresh so return the step
          return newStep;
        } else if (isNewStepInformational) {
          // Complete the other informational step to begin moving forward
          await completeAndGetNextStep(newStep.step.journeyStepId, {}, params);
        }
        return await requestInformationalStep('DownloadExtension');
      }
    }

    // No informational steps found, so return null
    return null;
  };

  /**
   * Analyzes journey steps for a specific stepId
   * @param {Array} steps - Array of journey step objects
   * @param {string} targetStepId - The stepId to analyze
   * @returns {Object} Analysis object with sortOrder, wasCompleted, needsCompleted, and exists
   */
  const analyzeJourneyStep = (steps, targetStepId) => {
    if (!steps || !Array.isArray(steps)) {
      return {
        sortOrder: -1,
        wasCompleted: false,
        needsCompleted: false,
        exists: false
      };
    }

    const matchingSteps = steps.filter(step => step.stepId === targetStepId);

    if (matchingSteps.length === 0) {
      return {
        sortOrder: -1,
        wasCompleted: false,
        needsCompleted: false,
        exists: false
      };
    }

    const highestSortOrder = Math.max(...matchingSteps.map(step => step.sortOrder || 0));
    const wasCompleted = matchingSteps.some(step => step.completedTime !== null);
    const needsCompleted = matchingSteps.some(step => step.completedTime === null);

    return {
      sortOrder: highestSortOrder,
      wasCompleted,
      needsCompleted,
      exists: true
    };
  };

  const fixJourney = async (currentStep) => {
    let needsFixing = false;
    try {
      const apiClient = getApiClient();
      const journey = await apiClient.getSessionJourney();

      // Get all analyses
      const analyses = {
        shareScreen: analyzeJourneyStep(journey.steps, 'ShareScreen'),
        shareWebcam: analyzeJourneyStep(journey.steps, 'ShareWebcam'),
        shareMicrophone: analyzeJourneyStep(journey.steps, 'ShareMicrophone')
      };

      // Find the highest sort order from all analyses
      const highestSortOrder = Math.max(
        analyses.shareScreen.sortOrder,
        analyses.shareWebcam.sortOrder,
        analyses.shareMicrophone.sortOrder
      );

      // If we havent passed the multimedia steps, then we can stop here
      if (highestSortOrder === -1 || highestSortOrder >= currentStep.step.sortOrder) return needsFixing;

      const stepsToUpsert = [];
      let sortOrder = highestSortOrder;
      // For each multimedia, we need ot see if it was completed, and if so, check it still exists
      if (
        analyses.shareScreen.exists &&
        analyses.shareScreen.wasCompleted &&
        !analyses.shareScreen.needsCompleted
      ) {
        const isScreenPublished = await isTrackPublished('screen');
        if (!isScreenPublished) {
          stepsToUpsert.push({
            stepId: 'ShareScreen',
            sortOrder,
            action: 'upsert'
          });
          sortOrder++;
        }
      }
      if (
        analyses.shareWebcam.exists &&
        analyses.shareWebcam.wasCompleted &&
        !analyses.shareWebcam.needsCompleted
      ) {
        const isWebcamPublished = await isTrackPublished('webcam');
        if (!isWebcamPublished) {
          stepsToUpsert.push({
            stepId: 'ShareWebcam',
            sortOrder,
            action: 'upsert'
          });
          sortOrder++;
        }
      }
      if (
        analyses.shareMicrophone.exists &&
        analyses.shareMicrophone.wasCompleted &&
        !analyses.shareMicrophone.needsCompleted
      ) {
        const isMicrophonePublished = await isTrackPublished('audio');
        if (!isMicrophonePublished) {
          stepsToUpsert.push({
            stepId: 'ShareMicrophone',
            sortOrder,
            action: 'upsert'
          });
          sortOrder++;
        }
      }

      if (stepsToUpsert.length > 0) {
        await apiClient.modifyJourneySteps({
          completeCurrentStep: false,
          pushUserToNextStep: true,
          addedBy: 'Platform',
          steps: stepsToUpsert
        });
        needsFixing = true;
      }
    } catch (error) {
      console.log('Error checking for session journey repairs', { error });
    }
    return needsFixing;
  };

  const handleStep = async (completeStep = false) => {
    const params = JSON.parse(sessionStorage.getItem('spParams'));
    if (!params) {
      navigate('/missing-params');
      return;
    }
    setLoading(true);
    let newStep = null;
    if (completeStep) {
      // Remove the next arrow
      setIsTaskComplete(false);
      // Complete the current step, and load the next
      newStep = await completeAndGetNextStep(
        step.journeyStepId,
        stepMetadataRef.current,
        params
      );
      // Clear the metadata for the step
      setStepMetadata({});
    } else {
      // Load the current step
      try {
        newStep = await getStep(params);
      } catch (error) {
        console.log('Error loading step', error.message);

        if (error instanceof ApiError && error.message && error.message.includes('401')) {
          setPageErrorText("Invalid token");
        } else {
          setPageErrorText("There was an problem loading this step.");
        }
      }
    }

    // Check if we need to load an informational step
    const informationalStep = await getInformationalStepToLoad(newStep, params);
    if (informationalStep) {
      // Load the informational step instead
      newStep = informationalStep;
    }

    // Check if we need to fix anything in the journey
    const needsFixing = await fixJourney(newStep);
    if (needsFixing) {
      setIsTaskComplete(false);
      setLoading(false);
      return;
    }

    if (newStep?.configurations) {
      await handleConfigurations(newStep.configurations);
    }
    // Set new task and remove the next arrow
    setStep(newStep.step);
    setIsTaskComplete(false);
    useAppStore.getState().setStep(newStep.step);
    useAppStore.getState().setPhase(newStep.phase);
    useAppStore.getState().setSessionStatus(newStep.session.status);
    // Stop the loading bar
    setLoading(false);
  };

  if (pageErrorText) {
    return (<PageError errorText={pageErrorText} />);
  }



  if (!pageErrorText && (!sessionStatus || validOnboardingStatuses.includes(sessionStatus.toLowerCase()))) {
    return (
      <>
        {step ? (
          <PaneViewer
            step={step}
            phase={phase}
            nextEnabled={isTaskComplete}
            inSession={inSession}
            isLoading={loading}
            setIsLoading={setLoading}
            onNext={() => handleStep(true)}
            setIsTaskComplete={setIsTaskComplete}
          >
            {loading && (
              <Flex
                direction="column"
                alignItems="center"
                justifyItems="center"
                height="100%"
              >
                <Loader text={loadingText || "Loading..."} size="large" />
              </Flex>
            )}
            {(!loading && !step) && (
              <Flex alignItems="center" justifyItems="center">
                <Text>No steps available</Text>
              </Flex>
            )}
            {(!loading && step) && renderStepComponent(
              step,
              setIsTaskComplete,
              handleStep,
              setStepMetadata,
              inSession
            )}
          </PaneViewer>
        ) : (
          <Flex
            direction="column"
            alignItems="center"
            justifyItems="center"
            height="100%"
          >
            <Loader text="Loading session data..."
              size="large"
              timeout={GET_STEP_TIMEOUT}
              onTimeout={() => {
                setPageErrorText('There was a problem loading this page.');
              }} />
          </Flex>
        )}
      </>
    );
  } else {
    return (
      <InvalidStatus />
    );
  }
};

export default Onboarding;
